import { useState } from "react";
import { Globe, ExternalLink } from "lucide-react";

const STATUS_COLORS = {
  Nuevo: "bg-blue-100 text-blue-700",
  Contactado: "bg-amber-100 text-amber-700",
  "Sesión agendada": "bg-purple-100 text-purple-700",
  Cerrado: "bg-emerald-100 text-emerald-700",
  Perdido: "bg-red-100 text-red-700",
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function LeadFormCard({ form, lastSubmissionAt }) {
  if (!form) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
          <Globe size={18} className="text-gray-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-700">
            Buzón web no configurado
          </p>
          <p className="text-xs text-gray-500">
            Este alumno aún no ha creado su formulario público.
          </p>
        </div>
      </div>
    );
  }

  const views = form.views_count || 0;
  const subs = form.submissions_count || 0;
  const rate = views > 0 ? Math.round((subs / views) * 100) : 0;
  const url = `${window.location.origin}/l/${form.slug}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
            <Globe size={18} className="text-emerald-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900">Buzón web</p>
              {form.is_active ? (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                  Activo
                </span>
              ) : (
                <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
                  Pausado
                </span>
              )}
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1 truncate"
            >
              /l/{form.slug}
              <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100">
        <Stat label="Vistas" value={views} />
        <Stat label="Envíos" value={subs} />
        <Stat label="Conversión" value={`${rate}%`} />
      </div>

      {lastSubmissionAt && (
        <p className="text-xs text-gray-400">
          Último envío: {formatDate(lastSubmissionAt)}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <p className="text-lg font-semibold text-gray-900">{value}</p>
      <p className="text-[11px] text-gray-500 uppercase tracking-wide">
        {label}
      </p>
    </div>
  );
}

export default function LeadsTab({ leads, leadForm }) {
  const [filter, setFilter] = useState("");

  const lastSubmissionAt = (leads || [])
    .filter((l) => l.source === "public_form")
    .map((l) => l.submitted_at || l.created_at)
    .filter(Boolean)
    .sort()
    .at(-1);

  if (leads.length === 0) {
    return (
      <div className="space-y-4">
        <LeadFormCard form={leadForm} lastSubmissionAt={lastSubmissionAt} />
        <div className="text-center py-12">
          <p className="text-gray-400">Este alumno no tiene leads registrados</p>
        </div>
      </div>
    );
  }

  const estados = [...new Set(leads.map((l) => l.estado).filter(Boolean))];
  const filtered = filter ? leads.filter((l) => l.estado === filter) : leads;

  return (
    <div className="space-y-4">
      <LeadFormCard form={leadForm} lastSubmissionAt={lastSubmissionAt} />
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {filtered.length} lead{filtered.length !== 1 ? "s" : ""}
        </p>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white"
        >
          <option value="">Todos los estados</option>
          {estados.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Nombre</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Resultado</th>
                <th className="px-4 py-2">Canal</th>
                <th className="px-4 py-2">Servicio</th>
                <th className="px-4 py-2">Contacto</th>
                <th className="px-4 py-2">Teléfono</th>
                <th className="px-4 py-2">Seguimiento</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((lead) => (
                <tr key={lead.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                    {lead.nombre || "—"}
                  </td>
                  <td className="px-4 py-2">
                    {lead.estado ? (
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          STATUS_COLORS[lead.estado] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {lead.estado}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {lead.resultado || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {lead.canal || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {lead.servicio || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                    {formatDate(lead.fecha_contacto)}
                  </td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                    {lead.telefono || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {lead.seguimiento || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
